import { getWebInstrumentations, initializeFaro, Faro } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import CONFIG from 'config';

const APP_NAME = 'member-web-app';

export function stripUrlQuery(value: unknown): unknown {
  return typeof value === 'string' ? value.split('?')[0] : value;
}

// Span attribute keys (OTLP semantic conventions) that can carry the full
// request URL, query string included.
const QUERY_STRING_ATTRIBUTE_KEYS = new Set(['http.url', 'http.target', 'url.full']);

// Minimal structural subset of the OTLP JSON trace payload (item.payload for a
// Faro TRACE transport item) that we need to walk. Kept local rather than
// importing @opentelemetry/otlp-transformer's internal types, which are not a
// published/stable API surface.
interface FaroSpanAttribute {
  key?: string;
  value?: { stringValue?: string };
}
interface FaroSpan {
  attributes?: FaroSpanAttribute[];
}
interface FaroScopeSpan {
  spans?: FaroSpan[];
}
interface FaroResourceSpan {
  scopeSpans?: FaroScopeSpan[];
}
interface FaroTracePayload {
  resourceSpans?: FaroResourceSpan[];
}

// PHI posture: captured page URLs never keep query strings. TracingInstrumentation
// also records outgoing fetch/XHR calls as OTLP spans (item.payload.resourceSpans);
// their http.url/http.target/url.full attributes retain the query string until
// stripped here too. Defense-in-depth only — project convention keeps auth in the
// Bearer header (never a query param), so no endpoint should be putting PHI in a
// query string in the first place, but we strip anyway before spans reach Tempo.
export function scrubEvent<T extends { meta?: { page?: { url?: string } } }>(item: T): T {
  if (item?.meta?.page?.url) {
    item.meta.page.url = stripUrlQuery(item.meta.page.url) as string;
  }
  const tracePayload = (item as unknown as { payload?: FaroTracePayload })?.payload;
  tracePayload?.resourceSpans?.forEach(resourceSpan => {
    resourceSpan?.scopeSpans?.forEach(scopeSpan => {
      scopeSpan?.spans?.forEach(span => {
        span?.attributes?.forEach(attribute => {
          if (
            attribute?.key &&
            QUERY_STRING_ATTRIBUTE_KEYS.has(attribute.key) &&
            typeof attribute.value?.stringValue === 'string'
          ) {
            attribute.value.stringValue = stripUrlQuery(attribute.value.stringValue) as string;
          }
        });
      });
    });
  });
  return item;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function initFaro(): Faro | null {
  // eslint-disable-next-line no-underscore-dangle
  const runtime = (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) || {};
  const url = runtime.REACT_APP_FARO_URL ?? process.env.REACT_APP_FARO_URL;
  if (!url) return null; // envs without a collector: hard no-op

  const apiBaseUrl: string | undefined = CONFIG?.api?.baseUrl;
  return initializeFaro({
    url,
    app: { name: APP_NAME, version: process.env.REACT_APP_VERSION ?? '0.0.0' },
    beforeSend: scrubEvent,
    instrumentations: [
      ...getWebInstrumentations({ captureConsole: false }),
      new TracingInstrumentation({
        instrumentationOptions: {
          // traceparent only toward our own API - never third parties (boundary
          // after the base prevents lookalike-suffix domains like api.foo.com.evil.com)
          propagateTraceHeaderCorsUrls: apiBaseUrl
            ? [new RegExp('^' + escapeRegExp(apiBaseUrl) + '(?:[/:?#]|$)')]
            : [],
        },
      }),
    ],
  });
}
